// src/components/ScoreboardAdmin/OsaekomiComponent.jsx
import React, { useEffect, useRef, useState } from "react";

const OsaekomiComponent = ({
  osaekomi,
  setOsaekomi,
  tapisId,
  competiteur,
  score,
  setScore,
  playGongSound,
  wazariAwaseteIppon = true,
}) => {
  const [isRunning, setIsRunning] = useState(false);
  const [yukoAdded, setYukoAdded] = useState(false);
  const [wazaAriAdded, setWazaAriAdded] = useState(false);
  const [ipponAdded, setIpponAdded] = useState(false);

  const intRef = useRef(null);

  // ✅ delta “actif” apporté par CET osaekomi (ce qu’on doit retirer quand on convertit)
  // - après 5s : { yuko: 1, wazari: 0 }
  // - après 10s : { yuko: 0, wazari: 1 }
  // - après 20s : { yuko: 0, wazari: 0 }
  const awardedRef = useRef({ yuko: 0, wazari: 0 });

  // Timer stable
  useEffect(() => {
    if (isRunning && osaekomi < 20) {
      if (!intRef.current) {
        intRef.current = setInterval(() => setOsaekomi((v) => v + 1), 1000);
      }
    } else {
      if (intRef.current) {
        clearInterval(intRef.current);
        intRef.current = null;
      }
    }
    return () => {
      if (intRef.current) {
        clearInterval(intRef.current);
        intRef.current = null;
      }
    };
  }, [isRunning, osaekomi, setOsaekomi]);

  // Seuils (robuste: >= au lieu de ===)
  useEffect(() => {
    // 5s => Yuko
    if (osaekomi >= 5 && osaekomi < 10 && !yukoAdded && !wazaAriAdded && !ipponAdded) {
      setScore((p) => ({ ...p, yuko: (p.yuko || 0) + 1 }));
      awardedRef.current = { yuko: 1, wazari: 0 };
      setYukoAdded(true);
    }

    // 10s => Waza-ari + retirer le yuko de CET osaekomi
    if (osaekomi >= 10 && osaekomi < 20 && !wazaAriAdded && !ipponAdded) {
      const yukoDelta = awardedRef.current.yuko || 0;

      // Est-ce que l’ajout de ce wazari fait 2 wazari => ippon ?
      const willAwasete =
        !!wazariAwaseteIppon && ((score?.wazari || 0) + 1 >= 2);

      // On fixe le delta AVANT l’update (pas d’effet de bord dans le setScore)
      awardedRef.current = willAwasete ? { yuko: 0, wazari: 0 } : { yuko: 0, wazari: 1 };

      setScore((p) => {
        const prevY = p.yuko || 0;
        const prevW = p.wazari || 0;
        const prevI = p.ippon || 0;

        // Retire uniquement le yuko que cet osaekomi a ajouté à 5s
        const nextY = Math.max(0, prevY - yukoDelta);

        // Ajoute le wazari à 10s
        const wAfterAdd = prevW + 1;

        if (willAwasete) {
          playGongSound();
          // Consomme 2 wazari et ajoute ippon (sans toucher aux yuko existants)
          return {
            ...p,
            yuko: nextY,
            wazari: Math.max(0, wAfterAdd - 2),
            ippon: prevI + 1,
          };
        }

        return { ...p, yuko: nextY, wazari: wAfterAdd };
      });

      setWazaAriAdded(true);
      setYukoAdded(false);

      // Si awasete => on stoppe, et on marque ipponAdded pour éviter un ippon à 20s
      if (willAwasete) {
        setIsRunning(false);
        setIpponAdded(true);
      }
    }

    // 20s => Ippon + retirer le wazari de CET osaekomi
    if (osaekomi >= 20 && !ipponAdded) {
      setIsRunning(false);
      playGongSound();

      const yukoDelta = awardedRef.current.yuko || 0;
      const wazariDelta = awardedRef.current.wazari || 0;

      // Reset delta: cet osaekomi est terminé
      awardedRef.current = { yuko: 0, wazari: 0 };

      setScore((p) => ({
        ...p,
        yuko: Math.max(0, (p.yuko || 0) - yukoDelta),
        wazari: Math.max(0, (p.wazari || 0) - wazariDelta),
        ippon: (p.ippon || 0) + 1,
      }));

      setIpponAdded(true);
      setWazaAriAdded(false);
      setYukoAdded(false);
    }
  }, [
    osaekomi,
    yukoAdded,
    wazaAriAdded,
    ipponAdded,
    setScore,
    playGongSound,
    score?.wazari,
    wazariAwaseteIppon,
  ]);

  const toggleOsaekomi = () => setIsRunning((v) => !v);

  const resetOsaekomi = () => {
    setIsRunning(false);
    setOsaekomi(0);
    setYukoAdded(false);
    setWazaAriAdded(false);
    setIpponAdded(false);
    // ✅ IMPORTANT : reset du delta pour ne pas “convertir” plus tard un ancien osaekomi
    awardedRef.current = { yuko: 0, wazari: 0 };
  };

  const tone =
    osaekomi >= 20
      ? "text-emerald-400"
      : osaekomi >= 10
      ? "text-amber-300"
      : osaekomi >= 5
      ? "text-sky-300"
      : "text-white";

  return (
    <div className="text-center sm:pr-4">
      <p className={`text-base sm:text-lg font-semibold ${tone}`}>
        Osaekomi : {osaekomi}s
      </p>
      <div className="flex justify-center gap-2 mt-1">
        <button
          className={`px-3 py-1.5 rounded-lg text-sm font-semibold ${
            isRunning ? "bg-indigo-600" : "bg-blue-600"
          } text-white hover:opacity-95 active:scale-[0.98]`}
          onClick={toggleOsaekomi}
        >
          {isRunning ? "Stop" : "Start"}
        </button>
        <button
          className="px-3 py-1.5 rounded-lg text-sm font-semibold bg-rose-600 text-white hover:opacity-95 active:scale-[0.98]"
          onClick={resetOsaekomi}
        >
          Reset
        </button>
      </div>
    </div>
  );
};

export default OsaekomiComponent;
